import { useTranslation } from 'react-i18next';
import { ReactComponent as HelpCircleIcon } from 'src/assets/tailwind/help-circle.svg';
import { ReactComponent as XCloseIcon } from 'src/assets/tailwind/x-close.svg';
import { Button } from 'src/components/ui/button';
import { Dialog, DialogContent, DialogFooter } from 'src/components/ui/dialog';
import { Icon } from 'src/components/ui/icon';
import { Typography } from 'src/components/ui/typography';
import { TRANSLATION_CONSTANTS as T } from 'src/utils/translations';

import type { UploadConflictDialogProps } from './types';



export const UploadConflictDialog: React.FC<UploadConflictDialogProps> = ({
  isOpen,
  onClose,
  conflict,
  destinationPath
}) => {
  const { t } = useTranslation();

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        hideDefaultClose
        className="tw:max-w-[680px] tw:p-2xl tw:rounded-3xl tw:gap-0"
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        {/* Header */}
        <div className="tw:flex tw:flex-col tw:gap-2xs tw:px-2xl tw:py-s">
          <Typography variant="h3" colorClass="tw:text-text-primary">
            {t(T.uploadDetail)}
          </Typography>
          {destinationPath && (
            <Typography variant="b2" colorClass="tw:text-text-secondary">
              {t(T.syncingFilesTo, { path: destinationPath })}
            </Typography>
          )}
        </div>

        {/* Close button */}
        <button
          type="button"
          onClick={onClose}
          className="tw:absolute tw:top-l tw:right-l tw:p-s tw:rounded-full tw:cursor-pointer tw:border-none tw:bg-transparent hover:tw:bg-secondary"
        >
          <Icon
            icon={XCloseIcon}
            sizeClass="tw:h-m tw:w-m"
            colorClass="tw:text-icon-primary"
          />
        </button>

        {/* Body */}
        <div className="tw:flex tw:flex-col tw:gap-l tw:p-2xl">
          <div className="tw:flex tw:gap-l tw:p-l tw:bg-warning tw:border tw:border-solid tw:border-border-warning tw:rounded-bw-2xl-3xl tw:shadow-e1">
            <Icon
              icon={HelpCircleIcon}
              sizeClass="tw:h-xl tw:w-xl"
              colorClass="tw:text-icon-warning"
              className="tw:shrink-0"
            />
            <div className="tw:flex tw:flex-col tw:gap-2xs tw:flex-1">
              <Typography variant="h4" colorClass="tw:text-text-primary">
                {t(T.filesBeingUploadedConflict, {
                  count: conflict.count,
                  owner: conflict.owner
                })}
              </Typography>
            </div>
          </div>
        </div>

        {/* Footer */}
        <DialogFooter className="tw:w-full">
          <div className="tw:flex tw:justify-between tw:items-center tw:w-full tw:px-2xl tw:py-m">
            <Button
              hierarchy="destructive"
              size="small"
              text={t(T.cancel)}
              onClick={onClose}
            />
            <Button
              hierarchy="primary"
              size="small"
              text={t(T.okay)}
              onClick={onClose}
            />
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
